import {Component, DestroyRef, ElementRef, OnInit, ViewChild, inject} from '@angular/core';
import {TitleCasePipe} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatTooltip} from '@angular/material/tooltip';
import {Router} from '@angular/router';
import {CelularMaskPipe} from '../../../../shared/pipe/celular-mask.pipe';
import {CpfCnpjMaskPipe} from '../../../../shared/pipe/cpf-cnpj-mask.pipe';
import {DisparoAutomaticoService} from '../../../../shared/service/disparo-automatico.service';
import {PlataformaService} from '../../../../shared/service/plataforma.service';
import {MsgService} from '../../../../shared/service/msg.service';
import {iconeStatusContatoDisparo, iconeIntegracaoContatoDisparo} from '../../../../shared/model/disparo-automatico/icone-contato-disparo.util';
import {DisparoAutomaticoVariavelModel} from '../../../../shared/model/disparo-automatico/disparo-automatico-variavel.model';
import {TemplateSalvamentoModel} from '../../../../shared/model/template/template-salvamento-model';
import {calcularSMS, montarMensagemSmsContato} from '../../../../shared/util/sms.utils';
import {CONTATO_DISPARO_FILTRO_SESSION_KEY} from '../../../../shared/util/chat-transferencia.constants';

export interface DadosModalBaseCompleta {
  contatos?: any[];
  colunasComplementares?: string[];
  disparoId?: number | null;
  baseId?: number | null;
  totalContatos?: number;
  tipoDisparo?: string;
  temIntegracao?: boolean;
  templateSms?: TemplateSalvamentoModel;
  variaveis?: DisparoAutomaticoVariavelModel[];
}

interface ColunaComplementarModal {
  id: string;
  nome: string;
}

@Component({
  selector: 'app-modal-base-completa',
  standalone: true,
  imports: [
    MatTableModule,
    MatTooltip,
    MatIconModule,
    MatProgressSpinner,
    TitleCasePipe,
    CelularMaskPipe,
    CpfCnpjMaskPipe
  ],
  templateUrl: './modal-base-completa.component.html',
  styleUrl: './modal-base-completa.component.css'
})
export class ModalBaseCompletaComponent implements OnInit {
  @ViewChild('tableContainer') tableContainerRef?: ElementRef<HTMLDivElement>;

  private readonly disparoService = inject(DisparoAutomaticoService);
  private readonly plataformaService = inject(PlataformaService);
  private readonly msgService = inject(MsgService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly PAGE_SIZE = 100;

  contatos: any[] = [];
  contatosTable = new MatTableDataSource<any>();
  colunasComplementares: ColunaComplementarModal[] = [];
  displayedColumns: string[] = ['status', 'nome', 'cpf', 'telefone', 'email'];
  totalContatos = 0;
  isCarregando = false;
  fimDaLista = false;
  tipoDisparo?: string;
  temIntegracao = false;
  private paginaAtual = 0;

  protected readonly iconeStatusContato = iconeStatusContatoDisparo;
  protected readonly iconeIntegracaoContato = iconeIntegracaoContatoDisparo;

  readonly data = inject<DadosModalBaseCompleta>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<ModalBaseCompletaComponent>);

  ngOnInit(): void {
    this.tipoDisparo = this.data.tipoDisparo;
    this.temIntegracao = this.data.temIntegracao ?? false;
    this.configurarColunas(this.data.colunasComplementares ?? []);

    if (this.data.baseId || this.data.disparoId) {
      this.carregarPagina();
      return;
    }

    this.contatos = this.data.contatos ?? [];
    if (!this.data.colunasComplementares?.length) {
      this.configurarColunas(this.inferirColunas(this.contatos[0]));
    }
    this.contatosTable.data = this.contatos;
    this.totalContatos = this.data.totalContatos ?? this.contatos.length;
    this.fimDaLista = true;
  }

  fechar(): void {
    this.dialogRef.close();
  }

  onScroll(): void {
    const elemento = this.tableContainerRef?.nativeElement;
    if (!elemento || this.isCarregando || this.fimDaLista) return;

    if (elemento.scrollTop + elemento.clientHeight >= elemento.scrollHeight - 200) {
      this.carregarPagina();
    }
  }

  nomeContato(contato: any): string {
    return contato?.nomeContato ?? contato?.nome ?? '--';
  }

  buscarCampoComplementar(contato: any, nomeColuna: string): string {
    const campos = contato?.camposComplementares;
    if (Array.isArray(campos)) {
      const campo = campos.find((item: any) =>
        (item.nome ?? item.nomeColuna)?.localeCompare(nomeColuna, 'pt', {sensitivity: 'base'}) === 0);
      return campo?.valor ?? '--';
    }
    if (campos && typeof campos === 'object') {
      const chave = Object.keys(campos).find(nome =>
        nome.localeCompare(nomeColuna, 'pt', {sensitivity: 'base'}) === 0);
      return chave ? (campos[chave] ?? '--') : '--';
    }
    return '--';
  }

  quantidadeSmsContato(contato: any): number {
    if ((this.tipoDisparo ?? '').toLowerCase() !== 'sms') return 1;
    const multiplicadorPersistido = Number(contato?.multiplicadorCustoSms);
    if (Number.isInteger(multiplicadorPersistido) && multiplicadorPersistido > 0) {
      return multiplicadorPersistido;
    }
    if (!this.data.templateSms) return 1;
    const mensagem = montarMensagemSmsContato(this.data.templateSms, this.data.variaveis ?? [], contato);
    return calcularSMS(mensagem).sms;
  }

  exibeAtalhoChat(contato: any): boolean {
    return !!this.data.disparoId
      && (this.tipoDisparo ?? '').toLowerCase() === 'whatsapp'
      && !!this.telefoneContato(contato);
  }

  abrirChatContato(contato: any): void {
    const busca = this.telefoneContato(contato);
    if (!busca) return;
    sessionStorage.setItem(CONTATO_DISPARO_FILTRO_SESSION_KEY, busca);
    const url = this.router.serializeUrl(this.router.createUrlTree(['/contato']));
    const aba = window.open(url, '_blank');
    sessionStorage.removeItem(CONTATO_DISPARO_FILTRO_SESSION_KEY);
    if (aba) aba.opener = null;
  }

  private telefoneContato(contato: any): string | null {
    return contato?.telefone?.toString().trim() || null;
  }

  mensagemCustoSms(contato: any): string {
    return `Este SMS custou ${this.quantidadeSmsContato(contato)}x o valor unitário pois ultrapassou o máx. de caracteres.`;
  }

  private carregarPagina(): void {
    if (this.isCarregando || this.fimDaLista) return;

    this.isCarregando = true;
    const requisicao = this.data.baseId
      ? this.plataformaService.buscarDadosBase(this.data.baseId, this.paginaAtual, this.PAGE_SIZE)
      : this.disparoService.buscarContatosPorDisparo(this.data.disparoId!, this.paginaAtual, this.PAGE_SIZE);

    requisicao.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const novos = res.contatos ?? res.content ?? [];
        if (this.paginaAtual === 0 && !this.data.colunasComplementares?.length) {
          this.configurarColunas(this.inferirColunas(novos[0]));
        }
        this.contatos = [...this.contatos, ...novos];
        this.contatosTable.data = this.contatos;
        this.totalContatos = res.totalElements ?? this.data.totalContatos ?? this.contatos.length;
        this.fimDaLista = res.last === true || novos.length < this.PAGE_SIZE;
        this.paginaAtual++;
        this.isCarregando = false;
      },
      error: () => {
        this.isCarregando = false;
        this.msgService.msgErro('Não foi possível carregar a base completa.');
      }
    });
  }

  private configurarColunas(nomes: string[]): void {
    this.colunasComplementares = nomes.map((nome, indice) => ({
      id: `complementar-${indice}`,
      nome
    }));
    this.displayedColumns = ['status', 'nome', 'cpf', 'telefone', 'email', ...this.colunasComplementares.map(coluna => coluna.id),];
  }

  private inferirColunas(contato: any): string[] {
    const campos = contato?.camposComplementares;
    if (Array.isArray(campos)) {
      return campos
        .map((campo: any) => campo.nome ?? campo.nomeColuna)
        .filter((nome: string | undefined): nome is string => !!nome);
    }
    return campos && typeof campos === 'object' ? Object.keys(campos) : [];
  }
}
